import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaQuestionCircle, FaBug, FaLightbulb, FaShieldAlt, FaDatabase, FaUserSecret } from 'react-icons/fa';

const Support = () => {
  const [cardBackgrounds, setCardBackgrounds] = useState({
    help: '',
    bug: '',
    feedback: ''
  });

  // Generate random background patterns for cards
  useEffect(() => {
    const patterns = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    ];

    const shuffled = [...patterns].sort(() => Math.random() - 0.5);
    setCardBackgrounds({
      help: shuffled[0],
      bug: shuffled[1],
      feedback: shuffled[2]
    });
  }, []);

  const handleEmailClick = () => {
    window.location.href = 'mailto:blatantspace@gmail.com?subject=Support Request';
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] pt-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-['VT323'] text-4xl md:text-6xl text-black/80 mb-4">
            Support
          </h1>
          <p className="font-['VT323'] text-xl text-black/60 max-w-2xl mx-auto">
            Need help? We're here for you. Get in touch with any questions, bug reports, or feedback.
          </p>
        </div>

        {/* Contact Button */}
        <div className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-8 mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <FaEnvelope className="text-black/40 text-xl" />
            <h2 className="font-['VT323'] text-2xl text-black/80">Get in Touch</h2>
          </div>
          
          <button
            onClick={handleEmailClick}
            className="bg-black/[0.08] hover:bg-black/[0.12] transition-all duration-200 px-8 py-4 rounded-lg font-['VT323'] text-xl text-black/60 hover:text-black/80"
          >
            📧 Send us an Email
          </button>
        </div>

        {/* Common Issues */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="relative bg-white/60 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6 overflow-hidden">
            <div 
              className="absolute inset-0 opacity-10"
              style={{ background: cardBackgrounds.help }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <FaQuestionCircle className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-xl text-black/80">General Help</h3>
              </div>
              <p className="font-['VT323'] text-lg text-black/60 mb-4">
                Questions about how to use Retro, account issues, or general inquiries.
              </p>
              <a 
                href="mailto:blatantspace@gmail.com?subject=General Help" 
                className="font-['VT323'] text-lg text-black/40 hover:text-black/60 transition-colors underline"
              >
                Email for Help →
              </a>
            </div>
          </div>

          <div className="relative bg-white/60 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6 overflow-hidden">
            <div 
              className="absolute inset-0 opacity-10"
              style={{ background: cardBackgrounds.bug }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <FaBug className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-xl text-black/80">Bug Reports</h3>
              </div>
              <p className="font-['VT323'] text-lg text-black/60 mb-4">
                Found a bug? Let us know and we'll fix it as soon as possible.
              </p>
              <a 
                href="mailto:blatantspace@gmail.com?subject=Bug Report" 
                className="font-['VT323'] text-lg text-black/40 hover:text-black/60 transition-colors underline"
              >
                Report Bug →
              </a>
            </div>
          </div>

          <div className="relative bg-white/60 backdrop-blur-xl rounded-lg border border-black/[0.08] p-6 overflow-hidden">
            <div 
              className="absolute inset-0 opacity-10"
              style={{ background: cardBackgrounds.feedback }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <FaLightbulb className="text-black/40 text-xl" />
                <h3 className="font-['VT323'] text-xl text-black/80">Feedback</h3>
              </div>
              <p className="font-['VT323'] text-lg text-black/60 mb-4">
                Have ideas for improvement? We'd love to hear your suggestions.
              </p>
              <a 
                href="mailto:blatantspace@gmail.com?subject=Feedback" 
                className="font-['VT323'] text-lg text-black/40 hover:text-black/60 transition-colors underline"
              >
                Send Feedback →
              </a>
            </div>
          </div>
        </div>

        {/* Privacy Policy Section */}
        <div className="bg-white/90 backdrop-blur-xl rounded-lg border border-black/[0.08] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FaShieldAlt className="text-black/40 text-xl" />
            <h2 className="font-['VT323'] text-3xl text-black/80">Privacy Policy</h2>
          </div>
          
          <p className="font-['VT323'] text-lg text-black/60 mb-6">
            Your privacy matters. Here's how we protect your data and what we do with it.
          </p>
          
          <p className="font-['VT323'] text-lg text-black/40 mb-8">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          {/* Key Points */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/[0.03] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <FaShieldAlt className="text-black/40 text-lg" />
                <h3 className="font-['VT323'] text-xl text-black/80">Data Protection</h3>
              </div>
              <p className="font-['VT323'] text-lg text-black/60">
                We use industry-standard security measures to protect your personal information and creative content.
              </p>
            </div>

            <div className="bg-black/[0.03] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <FaUserSecret className="text-black/40 text-lg" />
                <h3 className="font-['VT323'] text-xl text-black/80">No Selling</h3>
              </div>
              <p className="font-['VT323'] text-lg text-black/60">
                We never sell your personal data or creative content to third parties. Your privacy is not for sale.
              </p>
            </div>
          </div>

          {/* Detailed Sections */}
          <div className="space-y-8">
            {/* Information We Collect */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FaDatabase className="text-black/40 text-lg" />
                <h3 className="font-['VT323'] text-2xl text-black/80">Information We Collect</h3>
              </div>
              
              <div className="space-y-4 ml-6">
                <div>
                  <h4 className="font-['VT323'] text-xl text-black/60 mb-2">Account Information</h4>
                  <p className="font-['VT323'] text-lg text-black/40">
                    Email address, name, and profile picture from your authentication provider (Google, Apple, etc.)
                  </p>
                </div>
                
                <div>
                  <h4 className="font-['VT323'] text-xl text-black/60 mb-2">Creative Content</h4>
                  <p className="font-['VT323'] text-lg text-black/40">
                    Images you generate, prompts you create, and preferences you set within the application
                  </p>
                </div>
                
                <div>
                  <h4 className="font-['VT323'] text-xl text-black/60 mb-2">Usage Data</h4>
                  <p className="font-['VT323'] text-lg text-black/40">
                    Basic analytics about how you use the service to improve functionality and user experience
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h3 className="font-['VT323'] text-2xl text-black/80 mb-4">How We Use Your Information</h3>
              
              <div className="space-y-3 ml-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    Provide and maintain the Retro service and generate images based on your prompts
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    Process payments and manage your account and subscription
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    Improve our service and develop new features based on usage patterns
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    Communicate with you about your account and provide customer support
                  </p>
                </div>
              </div>
            </div>

            {/* Data Sharing */}
            <div>
              <h3 className="font-['VT323'] text-2xl text-black/80 mb-4">Data Sharing</h3>
              
              <p className="font-['VT323'] text-lg text-black/60 mb-4 ml-6">
                We only share your information in these limited circumstances:
              </p>
              
              <div className="space-y-3 ml-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Service Providers:</strong> Third-party services that help us operate (hosting, payment processing, email)
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Legal Requirements:</strong> When required by law or to protect our rights and users' safety
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Public Content:</strong> Images you choose to make public are visible to other users
                  </p>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div>
              <h3 className="font-['VT323'] text-2xl text-black/80 mb-4">Your Rights</h3>
              
              <div className="space-y-3 ml-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Access:</strong> Request a copy of your personal data
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Correction:</strong> Update or correct your information
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-black/40 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="font-['VT323'] text-lg text-black/60">
                    <strong>Deletion:</strong> Request deletion of your account and data
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Contact */}
            <div className="bg-black/[0.03] rounded-lg p-6">
              <h3 className="font-['VT323'] text-2xl text-black/80 mb-4">Questions About Privacy?</h3>
              
              <p className="font-['VT323'] text-lg text-black/60 mb-4">
                If you have questions about this privacy policy or how we handle your data, contact us:
              </p>
              
              <button
                onClick={() => window.location.href = 'mailto:blatantspace@gmail.com?subject=Privacy Question'}
                className="bg-black/[0.08] hover:bg-black/[0.12] transition-all duration-200 px-6 py-3 rounded-lg font-['VT323'] text-lg text-black/60 hover:text-black/80"
              >
                📧 Privacy Questions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support; 